import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { invalidateSiteSettings } from '@/lib/siteSettings';
import { toast } from 'sonner';
import { Save, ArrowLeft, Eye, Palette, Type, Image as ImageIcon, Layout } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SiteCustomization = () => {
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    site_name: '',
    about_title: '',
    gallery_title: '',
    contact_title: '',
    contact_description: '',
    contact_location: '',
    contact_email: '',
    contact_phone: '',
    contact_notable_works: '',
    primary_color: '#D97706',
    background_color: '#F5F5F0',
    text_color: '#1C1917',
    font_family: 'Cormorant Garamond',
    custom_css: '',
    footer_text: '',
    hero_image_url: '',
    hero_videos: []
  });
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [logoImageFile, setLogoImageFile] = useState(null);
  const [heroTitleImageFile, setHeroTitleImageFile] = useState(null);
  const [heroVideo1File, setHeroVideo1File] = useState(null);
  const [heroVideo2File, setHeroVideo2File] = useState(null);
  const [video1Title, setVideo1Title] = useState('Awards');
  const [video2Title, setVideo2Title] = useState('Paintings');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ video1: 0, video2: 0 });
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const CHUNK_SIZE = 512 * 1024; // 512KB chunks

  const uploadVideoChunked = async (file, slot, title) => {
    const token = localStorage.getItem('admin_token');
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const filename = `${slot}_${Date.now()}`;
    const progressKey = slot === 'video1' ? 'video1' : 'video2';

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk, `${filename}.part${i}`);
      formData.append('filename', filename);
      formData.append('chunk_index', i);
      formData.append('total_chunks', totalChunks);

      const res = await axios.post(`${API}/upload/video-chunk`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const progress = Math.round(((i + 1) / totalChunks) * 100);
      setUploadProgress(prev => ({ ...prev, [progressKey]: progress }));

      if (res.data.status === 'complete') {
        // Save the video URL to settings
        const saveForm = new FormData();
        saveForm.append('video_url', res.data.url);
        saveForm.append('video_slot', slot);
        saveForm.append('video_title', title);
        await axios.post(`${API}/upload/save-video`, saveForm, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return res.data.url;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const token = localStorage.getItem('admin_token');

    try {
      // Upload videos first using chunked upload
      if (heroVideo1File) {
        setIsUploadingVideo(true);
        toast.info('Uploading Video 1...');
        await uploadVideoChunked(heroVideo1File, 'video1', video1Title);
        toast.success('Video 1 uploaded!');
        setHeroVideo1File(null);
      }

      if (heroVideo2File) {
        setIsUploadingVideo(true);
        toast.info('Uploading Video 2...');
        await uploadVideoChunked(heroVideo2File, 'video2', video2Title);
        toast.success('Video 2 uploaded!');
        setHeroVideo2File(null);
      }

      setIsUploadingVideo(false);
      setUploadProgress({ video1: 0, video2: 0 });

      // Now save text/color/font settings
      const formData = new FormData();

      const allowedFields = [
        'hero_title', 'hero_subtitle', 'site_name', 'about_title', 'gallery_title',
        'contact_title', 'contact_description', 'contact_location', 'contact_email',
        'contact_phone', 'contact_notable_works', 'chatbot_welcome_message',
        'primary_color', 'background_color', 'text_color', 'font_family',
        'custom_css', 'footer_text'
      ];

      allowedFields.forEach(key => {
        if (settings[key] !== null && settings[key] !== undefined && settings[key] !== '') {
          formData.append(key, settings[key]);
        }
      });

      if (settings.chatbot_enabled !== null && settings.chatbot_enabled !== undefined) {
        formData.append('chatbot_enabled', settings.chatbot_enabled);
      }

      if (heroImageFile) {
        formData.append('hero_image', heroImageFile);
      }
      if (logoImageFile) {
        formData.append('logo_image', logoImageFile);
      }
      if (heroTitleImageFile) {
        formData.append('hero_title_image', heroTitleImageFile);
      }

      await axios.put(`${API}/settings`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      invalidateSiteSettings();
      toast.success('Settings saved successfully! Refresh the site to see changes.');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSubmitting(false);
      setIsUploadingVideo(false);
    }
  };

  const colorPresets = [
    { name: 'Saffron Gold', color: '#D97706' },
    { name: 'Royal Blue', color: '#1E40AF' },
    { name: 'Emerald Green', color: '#059669' },
    { name: 'Deep Purple', color: '#7C3AED' },
    { name: 'Crimson Red', color: '#DC2626' },
    { name: 'Teal', color: '#0D9488' }
  ];

  const fontOptions = [
    'Cormorant Garamond',
    'Playfair Display',
    'Merriweather',
    'Lora',
    'Crimson Text',
    'EB Garamond',
    'Manrope',
    'Inter',
    'Poppins'
  ];

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="site-customization-page">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              data-testid="back-button"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">
                Site Customization
              </h1>
              <p className="font-sans text-muted-foreground mt-2">
                Customize every aspect of your website
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => window.open('/', '_blank')}
              variant="outline"
              data-testid="preview-button"
            >
              <Eye size={16} className="mr-2" />
              Preview Site
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="content" className="space-y-8">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="content" className="flex items-center gap-2">
                <Layout size={16} />
                Content
              </TabsTrigger>
              <TabsTrigger value="contact" className="flex items-center gap-2">
                <Layout size={16} />
                Contact
              </TabsTrigger>
              <TabsTrigger value="design" className="flex items-center gap-2">
                <Palette size={16} />
                Design
              </TabsTrigger>
              <TabsTrigger value="hero" className="flex items-center gap-2">
                <ImageIcon size={16} />
                Hero Section
              </TabsTrigger>
              <TabsTrigger value="advanced" className="flex items-center gap-2">
                <Type size={16} />
                Advanced
              </TabsTrigger>
            </TabsList>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-6">
              <div className="bg-card rounded-lg p-6 space-y-6">
                <h2 className="font-serif text-2xl font-light mb-4">Page Titles & Text</h2>
                
                <div>
                  <Label htmlFor="site_name">Site Name (Logo Text Fallback)</Label>
                  <Input
                    id="site_name"
                    value={settings.site_name}
                    onChange={(e) => handleChange('site_name', e.target.value)}
                    placeholder="Gangadhar Som"
                  />
                </div>

                <div className="border border-border rounded-lg p-4 space-y-3">
                  <Label htmlFor="logo_image">Logo Image (replaces text logo in nav)</Label>
                  <p className="text-sm text-muted-foreground">Upload a custom signature, calligraphy, or logo image. Transparent PNG recommended.</p>
                  <Input
                    id="logo_image"
                    type="file"
                    accept="image/*"
                    data-testid="logo-image-upload"
                    onChange={(e) => setLogoImageFile(e.target.files[0])}
                  />
                  {logoImageFile && (
                    <p className="text-sm text-accent">Selected: {logoImageFile.name}</p>
                  )}
                  {settings.logo_image_url && !logoImageFile && (
                    <div className="mt-2">
                      <Label>Current Logo</Label>
                      <div className="mt-1 p-3 bg-foreground/5 rounded inline-block">
                        <img src={settings.logo_image_url} alt="Current logo" className="h-12 w-auto object-contain" />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="hero_title">Hero Section Title</Label>
                  <Input
                    id="hero_title"
                    value={settings.hero_title}
                    onChange={(e) => handleChange('hero_title', e.target.value)}
                    placeholder="Gangadhar Som"
                  />
                </div>

                <div>
                  <Label htmlFor="hero_subtitle">Hero Section Subtitle</Label>
                  <Textarea
                    id="hero_subtitle"
                    value={settings.hero_subtitle}
                    onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                    rows={3}
                    placeholder="Artist description..."
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="about_title">About Page Title</Label>
                    <Input
                      id="about_title"
                      value={settings.about_title}
                      onChange={(e) => handleChange('about_title', e.target.value)}
                      placeholder="About the Artist"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gallery_title">Gallery Page Title</Label>
                    <Input
                      id="gallery_title"
                      value={settings.gallery_title}
                      onChange={(e) => handleChange('gallery_title', e.target.value)}
                      placeholder="Gallery"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_title">Contact Page Title</Label>
                    <Input
                      id="contact_title"
                      value={settings.contact_title}
                      onChange={(e) => handleChange('contact_title', e.target.value)}
                      placeholder="Contact"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="footer_text">Footer Text (Optional)</Label>
                  <Input
                    id="footer_text"
                    value={settings.footer_text || ''}
                    onChange={(e) => handleChange('footer_text', e.target.value)}
                    placeholder="© 2024 Gangadhar Som. All rights reserved."
                  />
                </div>
              </div>
            </TabsContent>

            {/* Contact Section Tab */}
            <TabsContent value="contact" className="space-y-6">
              <div className="bg-card rounded-lg p-6 space-y-6">
                <h2 className="font-serif text-2xl font-light mb-4">Contact Page Settings</h2>
                
                <div>
                  <Label htmlFor="contact_description">Contact Page Description</Label>
                  <Textarea
                    id="contact_description"
                    value={settings.contact_description}
                    onChange={(e) => handleChange('contact_description', e.target.value)}
                    rows={4}
                    placeholder="For inquiries about artwork, commissions, or exhibitions..."
                  />
                </div>

                <div>
                  <Label htmlFor="contact_location">Location / Address</Label>
                  <Input
                    id="contact_location"
                    value={settings.contact_location}
                    onChange={(e) => handleChange('contact_location', e.target.value)}
                    placeholder="Bangalore & Mysore, Karnataka, India"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_email">Email Address (Optional)</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={settings.contact_email || ''}
                      onChange={(e) => handleChange('contact_email', e.target.value)}
                      placeholder="artist@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Phone Number (Optional)</Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={settings.contact_phone || ''}
                      onChange={(e) => handleChange('contact_phone', e.target.value)}
                      placeholder="+91 1234567890"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_notable_works">Notable Works / Additional Info</Label>
                  <Textarea
                    id="contact_notable_works"
                    value={settings.contact_notable_works}
                    onChange={(e) => handleChange('contact_notable_works', e.target.value)}
                    rows={3}
                    placeholder="46 Lord Hanuman life history paintings at Karya Siddhi Hanuman Temple, Dallas, Texas, USA"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    This appears in the "Notable Works" section on the contact page
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Design Tab */}
            <TabsContent value="design" className="space-y-6">
              <div className="bg-card rounded-lg p-6 space-y-6">
                <h2 className="font-serif text-2xl font-light mb-4">Colors & Typography</h2>
                
                <div>
                  <Label>Primary Color (Accent)</Label>
                  <div className="flex gap-4 items-center mt-2">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-20 h-12"
                    />
                    <Input
                      type="text"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      placeholder="#D97706"
                      className="flex-1"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    {colorPresets.map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => handleChange('primary_color', preset.color)}
                        className="w-10 h-10 rounded-full border-2 border-border hover:scale-110 transition-transform"
                        style={{ backgroundColor: preset.color }}
                        title={preset.name}
                      />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Background Color</Label>
                    <div className="flex gap-4 items-center mt-2">
                      <Input
                        type="color"
                        value={settings.background_color}
                        onChange={(e) => handleChange('background_color', e.target.value)}
                        className="w-20 h-12"
                      />
                      <Input
                        type="text"
                        value={settings.background_color}
                        onChange={(e) => handleChange('background_color', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Text Color</Label>
                    <div className="flex gap-4 items-center mt-2">
                      <Input
                        type="color"
                        value={settings.text_color}
                        onChange={(e) => handleChange('text_color', e.target.value)}
                        className="w-20 h-12"
                      />
                      <Input
                        type="text"
                        value={settings.text_color}
                        onChange={(e) => handleChange('text_color', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="font_family">Heading Font Family</Label>
                  <select
                    id="font_family"
                    value={settings.font_family}
                    onChange={(e) => handleChange('font_family', e.target.value)}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {fontOptions.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                  <p className="text-sm text-muted-foreground mt-2">
                    Preview: <span style={{ fontFamily: settings.font_family }} className="text-2xl">Gangadhar Som</span>
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Hero Section Tab */}
            <TabsContent value="hero" className="space-y-6">
              <div className="bg-card rounded-lg p-6 space-y-6">
                <h2 className="font-serif text-2xl font-light mb-4">Hero Background Media</h2>

                <div className="border border-border rounded-lg p-4 space-y-3 mb-6">
                  <Label htmlFor="hero_title_image">Hero Title Image (replaces "Gangadhar Som" heading)</Label>
                  <p className="text-sm text-muted-foreground">Upload a custom calligraphy or artistic title image for the hero section. Transparent PNG recommended. This replaces the text heading.</p>
                  <Input
                    id="hero_title_image"
                    type="file"
                    accept="image/*"
                    data-testid="hero-title-image-upload"
                    onChange={(e) => setHeroTitleImageFile(e.target.files[0])}
                  />
                  {heroTitleImageFile && (
                    <p className="text-sm text-accent">Selected: {heroTitleImageFile.name}</p>
                  )}
                  {settings.hero_title_image_url && !heroTitleImageFile && (
                    <div className="mt-2">
                      <Label>Current Hero Title Image</Label>
                      <div className="mt-1 p-3 bg-foreground/90 rounded inline-block">
                        <img src={settings.hero_title_image_url} alt="Current hero title" className="h-16 w-auto object-contain" />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-accent pl-4 bg-accent/5 p-4 rounded">
                    <h3 className="font-sans font-semibold mb-2">📹 Video Background (Recommended)</h3>
                    <p className="text-sm text-muted-foreground">
                      Upload slow-motion videos for a dynamic, professional homepage. Videos auto-rotate every 10 seconds.
                    </p>
                  </div>

                  {/* Video 1 */}
                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-sans font-semibold mb-3">Video 1: Awards</h3>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="video1_title">Video Title</Label>
                        <Input
                          id="video1_title"
                          value={video1Title}
                          onChange={(e) => setVideo1Title(e.target.value)}
                          placeholder="Awards"
                        />
                      </div>
                      <div>
                        <Label htmlFor="hero_video_1">Upload Video 1</Label>
                        <Input
                          id="hero_video_1"
                          type="file"
                          accept="video/*"
                          onChange={(e) => setHeroVideo1File(e.target.files[0])}
                          className="mt-2"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Recommended: MP4, max 50MB, slow-motion footage of awards
                        </p>
                        {uploadProgress.video1 > 0 && uploadProgress.video1 < 100 && (
                          <div className="mt-2">
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${uploadProgress.video1}%` }} />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Uploading: {uploadProgress.video1}%</p>
                          </div>
                        )}
                        {heroVideo1File && (
                          <p className="text-sm text-accent mt-2">
                            Selected: {heroVideo1File.name} ({(heroVideo1File.size / (1024 * 1024)).toFixed(1)}MB)
                          </p>
                        )}
                      </div>
                      {settings.hero_videos && settings.hero_videos[0] && (
                        <div>
                          <Label>Current Video 1</Label>
                          <video
                            src={settings.hero_videos[0].url.startsWith('/') ? `${BACKEND_URL}${settings.hero_videos[0].url}` : settings.hero_videos[0].url}
                            controls
                            className="w-full h-40 rounded mt-2"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Video 2 */}
                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-sans font-semibold mb-3">Video 2: Paintings</h3>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="video2_title">Video Title</Label>
                        <Input
                          id="video2_title"
                          value={video2Title}
                          onChange={(e) => setVideo2Title(e.target.value)}
                          placeholder="Paintings"
                        />
                      </div>
                      <div>
                        <Label htmlFor="hero_video_2">Upload Video 2</Label>
                        <Input
                          id="hero_video_2"
                          type="file"
                          accept="video/*"
                          onChange={(e) => setHeroVideo2File(e.target.files[0])}
                          className="mt-2"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Recommended: MP4, max 50MB, slow-motion footage of paintings
                        </p>
                        {uploadProgress.video2 > 0 && uploadProgress.video2 < 100 && (
                          <div className="mt-2">
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${uploadProgress.video2}%` }} />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Uploading: {uploadProgress.video2}%</p>
                          </div>
                        )}
                        {heroVideo2File && (
                          <p className="text-sm text-accent mt-2">
                            Selected: {heroVideo2File.name} ({(heroVideo2File.size / (1024 * 1024)).toFixed(1)}MB)
                          </p>
                        )}
                      </div>
                      {settings.hero_videos && settings.hero_videos[1] && (
                        <div>
                          <Label>Current Video 2</Label>
                          <video
                            src={settings.hero_videos[1].url.startsWith('/') ? `${BACKEND_URL}${settings.hero_videos[1].url}` : settings.hero_videos[1].url}
                            controls
                            className="w-full h-40 rounded mt-2"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-sans font-semibold mb-3">🖼️ Static Image (Fallback)</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      If no videos are uploaded, a static image will be displayed
                    </p>
                    <div>
                      <Label htmlFor="hero_image">Upload Fallback Image</Label>
                      <Input
                        id="hero_image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="mt-2"
                      />
                      <p className="text-sm text-muted-foreground mt-2">
                        Recommended size: 1920x1080px or larger
                      </p>
                    </div>

                    {(previewImage || settings.hero_image_url) && (
                      <div className="mt-3">
                        <Label>Current/Preview Image</Label>
                        <div className="mt-2 rounded-lg overflow-hidden border border-border">
                          <img
                            src={previewImage || settings.hero_image_url}
                            alt="Hero preview"
                            className="w-full h-64 object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Advanced Tab */}
            <TabsContent value="advanced" className="space-y-6">
              <div className="bg-card rounded-lg p-6 space-y-6">
                <h2 className="font-serif text-2xl font-light mb-4">Custom CSS</h2>
                
                <div>
                  <Label htmlFor="custom_css">Custom CSS Code (Advanced)</Label>
                  <Textarea
                    id="custom_css"
                    value={settings.custom_css || ''}
                    onChange={(e) => handleChange('custom_css', e.target.value)}
                    rows={10}
                    placeholder=".hero-section { background-color: #000; }"
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Add custom CSS to override default styles. Use with caution!
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Save Button */}
          <div className="flex justify-end gap-4 mt-8 sticky bottom-8 bg-background p-4 rounded-lg border border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => fetchSettings()}
            >
              Reset Changes
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isUploadingVideo}
              className="bg-accent hover:bg-accent/90"
              data-testid="save-settings-button"
            >
              <Save size={16} className="mr-2" />
              {isUploadingVideo ? 'Uploading Videos...' : isSubmitting ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SiteCustomization;